import { useMutation, useQuery } from "@tanstack/react-query"
import { QueryKeys } from "./querykeys"
import businessApi from "../api/business.api"
import organizationApi from "../api/organization.api"
import { UserAuth } from "../context/AuthContext"
import { ICreateBusiness, ICreateRepresentanteLegal } from "../models/Business"


export const useQueryGetAllBusinesses = () => {
  const { isAuthenticated } = UserAuth()

  return (
    useQuery({
      enabled: isAuthenticated,
      queryKey: [QueryKeys.GET_BUSINESSES_ALL],
      queryFn: async () => {
        try {
          const res = await organizationApi.getMyBusinesses()
          if (res.status === 200) {
            return res.data
          }
        } catch (error) {
          return null
        }
      }
    })
  )
}


export const useMutationCreateBusiness = () => {
  return (
    useMutation({
      mutationFn: async (payload: ICreateBusiness) => {
        const res = await businessApi.createBusiness(payload)
        if (res.status === 201 || res.status === 200) {
          return res.data
        }
        throw new Error('No se pudo crear la empresa')
      }
    })
  )
}

export const useMutationCreateRepresentanteLeal = () => {
  return (
    useMutation({
      mutationFn: async ({ businessId, payload }: { businessId: number, payload: ICreateRepresentanteLegal }) => {
        const res = await businessApi.createRepresentanteLegal(businessId, payload)
        if (res.status === 201 || res.status === 200 || res.status === 204) {
          return res.data
        }
        throw new Error('No se pudo crear el representante legal')
      }
    })
  )
}

export const useGetDocumentsTypes = (businessId: number, year: number, month: number, type: string) => {
  return useQuery({
    queryKey: [QueryKeys.GET_DOCUMENTS_TYPES, businessId, month, year, type],
    queryFn: async () => {
      try {
        const res = await businessApi.getDocumentsTypes({ businessId, year, month, type })
        return res.data
      } catch {
        return []
      }
    },
    enabled: !!businessId
  })
}

const normalizeAccountTypes = (payload: unknown) => {
  const response = payload as Record<string, unknown> | unknown[] | null
  const rows = Array.isArray(response)
    ? response
    : (response?.accountsTypes ?? response?.accountTypes ?? response?.cuentas ?? response?.data ?? [])

  if (!Array.isArray(rows)) return []

  const uniqueOptions = new Map<string, { value: string; label: string }>()
  rows.forEach(item => {
    const record = item && typeof item === 'object' ? item as Record<string, unknown> : null
    const rawValue = record
      ? record.value ?? record.nombreCuenta ?? record.name ?? record.nombre ?? record.label
      : item
    const rawLabel = record
      ? record.label ?? record.nombreCuenta ?? record.name ?? record.nombre ?? record.value
      : item
    const value = String(rawValue ?? '').trim()
    const label = String(rawLabel ?? value).trim()
    const key = value.toLocaleLowerCase('es')

    if (value && !uniqueOptions.has(key)) uniqueOptions.set(key, { value, label })
  })

  return [...uniqueOptions.values()].sort((a, b) =>
    a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
  )
}

export const useGetAccountsTypes = (
  businessId: number,
  year: number,
  month: number,
  type: string
) => {
  return useQuery({
    queryKey: [QueryKeys.GET_ACCOUNTS_TYPES, businessId, month, year, type],
    queryFn: async () => {
      try {
        const res = await businessApi.getAccountsTypes({ businessId, year, month, type })
        return normalizeAccountTypes(res.data)
      } catch {
        return []
      }
    },
    enabled: !!businessId
  })
}
